import {
  differenceInCalendarDays,
  startOfDay,
  startOfMonth,
  addDays,
  addMonths,
  isAfter,
  format,
} from 'date-fns';

// Event Type filter options for the Usage Analytics report bar. Each option
// maps to one or more series keys returned by USAGE_ACTIVITY_OVER_TIME, so
// changing the filter just re-selects which series render (no refetch).
// Grounded in the event types actually written to ActivityLogs today:
// AUTH/SSO_AUTH (logins), AI_USAGE object_id 1/2 (text/voice), DATA_UPSERT
// (edits), DELETE, EXPORT.
// Event Type focus filter for the top Activity chart. 'ALL' is special-cased
// in UsageReportTab (core series by default + a "show all events" toggle);
// every other option focuses the chart on a single series key.
export const EVENT_TYPES = [
  { value: 'ALL', label: 'All Events', keys: [] },
  { value: 'LOGINS', label: 'Logins', keys: ['logins'] },
  { value: 'NOTES', label: 'Activity Notes', keys: ['notes'] },
  { value: 'AI_ALL', label: 'AI — Text & Voice', keys: ['ai_text', 'ai_voice'] },
  { value: 'AI_TEXT', label: 'AI — Text', keys: ['ai_text'] },
  { value: 'AI_VOICE', label: 'AI — Voice', keys: ['ai_voice'] },
  { value: 'VIEWS', label: 'Record Views', keys: ['views'] },
  { value: 'DATA_EDITS', label: 'Data Edits — All', keys: ['data_edits'] },
  { value: 'EDITS_PROVIDERS', label: 'Edits — Providers', keys: ['edits_providers'] },
  { value: 'EDITS_CONTACTS', label: 'Edits — Contacts', keys: ['edits_contacts'] },
  { value: 'EDITS_LOCATIONS', label: 'Edits — Locations', keys: ['edits_locations'] },
  { value: 'EDITS_MARKETS', label: 'Edits — Markets', keys: ['edits_markets'] },
  { value: 'EDITS_LABELS', label: 'Edits — Labels', keys: ['edits_labels'] },
  { value: 'EDITS_ORGANIZATIONS', label: 'Edits — Organizations', keys: ['edits_organizations'] },
  { value: 'FAILED_LOGINS', label: 'Failed Logins', keys: ['failed_logins'] },
  { value: 'UNAUTHORIZED', label: 'Unauthorized', keys: ['unauthorized'] },
  { value: 'DELETES', label: 'Deletes', keys: ['deletes'] },
  { value: 'EXPORTS', label: 'Exports / Downloads', keys: ['exports'] },
];

// Display names for series keys.
export const SERIES_LABELS = {
  logins: 'Logins',
  notes: 'Activity Notes',
  views: 'Record Views',
  failed_logins: 'Failed Logins',
  unauthorized: 'Unauthorized',
  ai_text: 'AI Text',
  ai_voice: 'AI Voice',
  data_edits: 'Data Edits',
  edits_providers: 'Provider Edits',
  edits_contacts: 'Contact Edits',
  edits_locations: 'Location Edits',
  edits_markets: 'Market Edits',
  edits_labels: 'Label Edits',
  edits_organizations: 'Organization Edits',
  deletes: 'Deletes',
  exports: 'Exports',
  active_users: 'Active Users',
  total: 'Total Events',
};

// Browser timezone as a MySQL-safe offset string (e.g. '-5:00', '+0:00').
// Matches the format validated by sanitizeTimezoneOffset() on the backend.
export function getTZOffset() {
  const mins = -new Date().getTimezoneOffset(); // e.g. EST -> -300
  const sign = mins >= 0 ? '+' : '-';
  const abs = Math.abs(mins);
  const hh = Math.floor(abs / 60);
  const mm = abs % 60;
  return `${sign}${hh}:${String(mm).padStart(2, '0')}`;
}

// Bucket granularity: daily for short ranges, monthly for longer ones.
export function getDateUnits(start, end) {
  return differenceInCalendarDays(end, start) > 62 ? 'months' : 'days';
}

// Ordered category buckets between start and end (inclusive). Keys are
// strings matching the backend DATE_FORMAT period ('YYYYMM' or 'YYYY-MM-DD').
export function buildCategories(start, end, unit = 'months') {
  const cats = [];
  let guard = 0;
  if (unit === 'days') {
    let m = startOfDay(start);
    const last = startOfDay(end);
    while (!isAfter(m, last) && guard < 400) {
      cats.push({ key: format(m, 'yyyy-MM-dd'), label: format(m, 'MMM d') });
      m = addDays(m, 1);
      guard += 1;
    }
  } else {
    let m = startOfMonth(start);
    const last = startOfMonth(end);
    while (!isAfter(m, last) && guard < 120) {
      cats.push({ key: format(m, 'yyyyMM'), label: format(m, 'MMM yy') });
      m = addMonths(m, 1);
      guard += 1;
    }
  }
  return cats;
}

// Align usage rows (each with a string `period`) to the category list,
// filling missing buckets with 0.
export function seriesFromRows(rows, cats, valueKey) {
  const byPeriod = new Map(
    (rows || []).map((r) => [String(r.period), Number(r[valueKey]) || 0])
  );
  return cats.map((c) => byPeriod.get(c.key) || 0);
}
